import { createSharedSupabaseClient } from "@shared/integrations/supabase";
import type { Database } from "@/types/supabase";

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

// AdminLooseDatabase: tipo permissivo usado pelo client `supabase` legacy.
// Mantido para evitar regressao em massa nos hooks que ainda nao foram migrados
// para o `Database` gerado.
//
// WPP-001.44: regenerar `src/types/supabase.ts` com `npm run types:supabase`
// (requer `supabase login` + `--project-id goptdjijvtvetifqkrsx`).
// Hooks novos devem importar `supabaseTyped` para ganhar checagem de schema
// em compile-time. Hooks legacy migram incrementalmente.
type LooseTable = {
  Row: Record<string, unknown>;
  Insert: Record<string, unknown>;
  Update: Record<string, unknown>;
  Relationships: [];
};

type LooseView = {
  Row: Record<string, unknown>;
  Relationships: [];
};

type LooseFunction = {
  Args: Record<string, unknown>;
  Returns: unknown;
};

type AdminLooseDatabase = {
  public: {
    Tables: Record<string, LooseTable>;
    Views: Record<string, LooseView>;
    Functions: Record<string, LooseFunction>;
    Enums: Record<string, string>;
    CompositeTypes: Record<string, never>;
  };
};

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error("Missing Supabase env vars: VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY");
}

export const supabase = createSharedSupabaseClient<AdminLooseDatabase>({
  url: supabaseUrl,
  anonKey: supabaseAnonKey,
  options: {
    auth: {
      autoRefreshToken: true,
      persistSession: true,
      detectSessionInUrl: true,
    },
  },
});

// `supabaseTyped` compartilha a mesma sessao do `supabase`, mas expoe o tipo
// `Database` real gerado a partir do schema. Use este client em hooks novos
// (ou ao tocar hooks legacy) para que erros tipo BUG-007 (.42) sejam pegos
// em build em vez de runtime.
export const supabaseTyped = createSharedSupabaseClient<Database>({
  url: supabaseUrl,
  anonKey: supabaseAnonKey,
  options: {
    auth: {
      autoRefreshToken: true,
      persistSession: true,
      detectSessionInUrl: true,
    },
  },
});

export type { Database };
